import { splitWhere } from './group';
import { wallTime } from './time';

export const CAMERAS = ['front', 'left_repeater', 'right_repeater', 'back', 'left_pillar', 'right_pillar'] as const;
export type Camera = (typeof CAMERAS)[number];

export type FolderKind = 'saved' | 'sentry' | 'recent' | 'clips';

export interface FileRef {
  readonly path: string;
  readonly size: number;
  readonly lastModified: number;
}

export interface Clip<F extends FileRef> {
  readonly ref: F;
  readonly camera: Camera;
  readonly slot: string;
  readonly slotTime: number;
}

export interface Footage<F extends FileRef> {
  readonly id: string;
  readonly dir: string;
  readonly name: string;
  readonly kind: FolderKind;
  readonly clips: readonly Clip<F>[];
  readonly eventJson: F | undefined;
  readonly aiJson: F | undefined;
  readonly thumb: F | undefined;
}

export interface Discovery<F extends FileRef> {
  readonly footage: readonly Footage<F>[];
  readonly videos: readonly F[];
}

const CLIP_NAME = /^(\d{4}-\d{2}-\d{2}_\d{2}-\d{2}-\d{2})-(front|back|left_repeater|right_repeater|left_pillar|right_pillar)\.mp4$/;
const STAMP = /^(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})-(\d{2})$/;
const VIDEO_NAME = /\.(mp4|m4v|mov)$/i;
const RUN_GAP_MS = 150_000;
const AI_NOTES_FILE = 'ai.json';
const AI_NOTES_SUFFIX = '.ai.json';
const KIND_BY_FOLDER: Readonly<Record<string, FolderKind>> = {
  saved: 'saved',
  savedclips: 'saved',
  sentry: 'sentry',
  sentryclips: 'sentry',
  recentclips: 'recent',
};

export function parseStamp(stamp: string): number | undefined {
  const match = STAMP.exec(stamp);
  return match ? wallTime(match.slice(1)) : undefined;
}

const splitPath = (path: string): { readonly dir: string; readonly name: string } => {
  const cut = path.lastIndexOf('/');
  return { dir: cut < 0 ? '' : path.slice(0, cut), name: path.slice(cut + 1) };
};

const folderKind = (dir: string): FolderKind =>
  dir.split('/').reduce<FolderKind>((kind, segment) => KIND_BY_FOLDER[segment.toLowerCase()] ?? kind, 'clips');

function toClip<F extends FileRef>(ref: F, name: string): Clip<F> | undefined {
  const match = CLIP_NAME.exec(name);
  if (!match) return undefined;
  const slot = match[1]!;
  return { ref, camera: match[2] as Camera, slot, slotTime: parseStamp(slot)! };
}

const splitRuns = <F extends FileRef>(clips: readonly Clip<F>[]): readonly (readonly Clip<F>[])[] =>
  splitWhere([...clips].sort((a, b) => a.slotTime - b.slotTime), (previous, clip) => clip.slotTime - previous.slotTime > RUN_GAP_MS);

interface Folder<F extends FileRef> {
  readonly clips: Clip<F>[];
  readonly namedAiJson: Map<string, F>;
  eventJson?: F;
  aiJson?: F;
  thumb?: F;
}

function collectFolders<F extends FileRef>(files: readonly F[]): { folders: Map<string, Folder<F>>; videos: F[] } {
  const folders = new Map<string, Folder<F>>();
  const videos: F[] = [];
  for (const ref of files) {
    const { dir, name } = splitPath(ref.path);
    const folder: Folder<F> = folders.get(dir) ?? { clips: [], namedAiJson: new Map<string, F>() };
    const clip = toClip(ref, name);
    if (clip) folder.clips.push(clip);
    else if (name === 'event.json') folder.eventJson = ref;
    else if (name === AI_NOTES_FILE) folder.aiJson = ref;
    else if (name.endsWith(AI_NOTES_SUFFIX)) folder.namedAiJson.set(name.slice(0, -AI_NOTES_SUFFIX.length), ref);
    else if (name === 'thumb.png') folder.thumb = ref;
    else if (VIDEO_NAME.test(name) && !name.startsWith('.')) videos.push(ref);
    folders.set(dir, folder);
  }
  return { folders, videos };
}

function folderFootage<F extends FileRef>(dir: string, folder: Folder<F>): readonly Footage<F>[] {
  const kind = folderKind(dir);
  const folderName = splitPath(dir).name;
  if (folder.eventJson) {
    const clips = [...folder.clips].sort((a, b) => a.slotTime - b.slotTime);
    const name = folderName || clips[0]?.slot || 'event';
    return [{ id: dir, dir, name, kind, clips, eventJson: folder.eventJson, aiJson: folder.aiJson ?? folder.namedAiJson.get(name), thumb: folder.thumb }];
  }
  const runs = splitRuns(folder.clips);
  return runs.map((clips) => ({
    id: `${dir}/${clips[0]!.slot}`,
    dir,
    name: clips[0]!.slot,
    kind,
    clips,
    eventJson: undefined,
    aiJson: folder.namedAiJson.get(clips[0]!.slot) ?? (runs.length === 1 ? folder.aiJson : undefined),
    thumb: undefined,
  }));
}

export function discover<F extends FileRef>(files: readonly F[]): Discovery<F> {
  const { folders, videos } = collectFolders(files);
  const footage = [...folders]
    .filter(([, folder]) => folder.clips.length > 0)
    .flatMap(([dir, folder]) => folderFootage(dir, folder))
    .sort((a, b) => a.clips[0]!.slotTime - b.clips[0]!.slotTime);
  return { footage, videos: footage.length > 0 ? [] : videos };
}

export const footageTime = <F extends FileRef>(footage: Footage<F>): number =>
  (footage.eventJson && parseStamp(footage.name)) ?? footage.clips.at(-1)!.slotTime;

export const aiNotesPath = <F extends FileRef>(footage: Footage<F>): string =>
  footage.eventJson ? `${footage.dir}/${AI_NOTES_FILE}` : `${footage.dir}/${footage.name}${AI_NOTES_SUFFIX}`;

export const clipKey = <F extends FileRef>(clip: Clip<F>): string => `${clip.slot}-${clip.camera}-${clip.ref.size}`;
